// File: src/App.js
import React, { useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import MetaPixelTracker from './components/MetaPixelTracker';
import { useAuth } from './context/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import Signup from './pages/Signup';
import Login from './pages/login';
import AddVehicle from './pages/AddVehicle';
import VehicleList from './pages/VehicleList';
import VehiclePage from './pages/VehiclePage';
import VehicleDetail from './pages/VehicleDetail';
import Profile from './pages/Profile';
import OwnerDashboard from './pages/OwnerDashboard';
import WorkshopDashboard from './pages/WorkshopDashboard';
import SupplierDashboard from './pages/SupplierDashboard';
import InsuranceDashboard from './pages/InsuranceDashboard';
import LoaderDashboard from './pages/LoaderDashboard';
import RequestWorkshop from './pages/RequestWorkshop';
import RequestLoader from './pages/RequestLoader';
import RequestQuotation from './pages/RequestQuotation';
import OwnerSupplierQuotationsPage from "./pages/OwnerSupplierQuotationsPage";
import AcceptedResponsesViewer from './pages/AcceptedResponsesViewer';
import ProtectedRoute from './components/ProtectedRoute';
import RespondQuotation from './pages/RespondQuotation';
import OemAssistant from './pages/OemAssistant';
import ViewMaintenance from './pages/ViewMaintenance';
import WorkshopLogMaintenance from './pages/WorkshopLogMaintenance';
import WorkshopCatalog from './pages/WorkshopCatalog';
import WorkshopRatings from './components/WorkshopRatings';
import CashbookLedger from './pages/CashbookLedger';
import CashbookForm from './components/CashbookForm';
import CategoryManager from './pages/CategoryManager';
import VehicleReceptionForm from './pages/VehicleReceptionForm';
import ChecklistViewer from './pages/ChecklistViewer';
import ReceptionChecklist from './pages/ReceptionChecklist';
import ReceptionHistory from './pages/ReceptionHistory';
import OwnerChecklistViewer from './pages/OwnerChecklistViewer';
import WorkshopQuotationsPage from './pages/WorkshopQuotationsPage';
import ViewRequests from './pages/ViewRequests';
import WorkshopInvoicesPage from './pages/WorkshopInvoicesPage';
import PaymentReceiptsPage from './pages/PaymentReceiptsPage';
import DeliveryNotesPage from './pages/DeliveryNotesPage';
import OwnerQuotationInvoiceView from "./components/OwnerQuotationInvoiceView";
import OwnerQuotationsPage from "./pages/OwnerQuotationsPage";
import OwnerInvoicesPage from './pages/OwnerInvoicesPage';
import OwnerInvoiceFromQuotationPage from './pages/OwnerInvoiceFromQuotationPage';
import WorkshopPurchaseOrdersPage from './pages/WorkshopPurchaseOrdersPage';
import SupplierMyQuotesPage from './pages/SupplierMyQuotesPage';
import SupplierPurchaseOrdersPage from './pages/SupplierPurchaseOrdersPage';
import WalkInRegistration from './pages/WalkInRegistration';
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import CatalogValidation from './pages/admin/CatalogValidation';
import AdminPartValidations from './pages/admin/PartValidations';
import FeatureManagement from './pages/admin/FeatureManagement';
import VerifyEmail from './pages/VerifyEmail';
import ActivateAccount from './pages/ActivateAccount';
import { PermissionsProvider } from './context/PermissionsContext';
import FeatureGuard from './components/FeatureGuard';
import FeatureGate from './components/FeatureGate';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import AdminFeatureFlags from './pages/admin/AdminFeatureFlags';
import UserValidation from './pages/admin/UserValidation';
import CreateAccount from './pages/admin/CreateAccount';
import TelematicsDevices from './pages/admin/TelematicsDevices';
import AdminCrashes from './pages/admin/AdminCrashes';
import OwnerLayout from './layouts/OwnerLayout';
import OwnerInvoiceByIdPage from './pages/OwnerInvoiceByIdPage';
import WorkshopProfile from './pages/WorkshopProfile';
import PublicWorkshopProfile from './pages/PublicWorkshopProfile';
import NotificationsPage from './pages/NotificationsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentErrorPage from './pages/PaymentErrorPage';
import Home from './pages/Home';

import WorkshopPrivateProfile from './pages/WorkshopPrivateProfile';
import WorkshopVehicleHistory from './pages/WorkshopVehicleHistory';
import { VehicleProvider } from './context/VehicleContext';
import MaintenanceHistoryWrapper from './pages/MaintenanceHistoryWrapper';
import WorkshopLayout from './components/WorkshopLayout';
import SupplierLayout from './components/SupplierLayout';
import SupplierInvoicesList from './pages/SupplierInvoicesList';
import NotFound from './pages/NotFound';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import FAQ from './pages/FAQ';
import Mission from './pages/Mission';
import About from './pages/About';
import Conditions from './pages/Conditions';
import Confidentialite from './pages/Confidentialite';
import SuppressionCompte from './pages/SuppressionCompte';
import ServicesSection from './pages/ServicesSection';

// ✅ Use existing detail component for supplier invoices
// ✅ Use existing detail component for supplier invoices
import SupplierInvoiceView from './pages/ClientInvoices';
import ClientSupplierInvoicesList from './pages/ClientSupplierInvoicesList'; // [NEW]

import './App.css';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Pages that have their own nav — hide the global one
const STANDALONE_ROUTES = ['/', '/login', '/signup', '/forgot-password', '/about', '/mission', '/faq', '/services', '/conditions', '/confidentialite', '/suppression-compte'];
const useIsStandalone = () => {
  const { pathname } = useLocation();
  return STANDALONE_ROUTES.includes(pathname) || pathname.startsWith('/verify-email') || pathname.startsWith('/reset-password') || pathname.startsWith('/activate/') || pathname.startsWith('/workshops/') || pathname.startsWith('/workshop/');
};

axios.interceptors.request.use(config => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-logout on expired/invalid token
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      // Don't redirect if already on login/signup/public pages
      if (!['/login', '/signup'].includes(currentPath) && !currentPath.startsWith('/verify-email') && !currentPath.startsWith('/workshops/')) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

const queryClient = new QueryClient();

const WithVehicleProvider = ({ children }) => (
  <VehicleProvider>{children}</VehicleProvider>
);

// Global nav with feature flag filtering for car owner links
const GlobalNav = ({ user, token, logout, getHomePath }) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const isOwner = user?.role === 'car owner';

  return (
    <nav className="flex flex-wrap items-center justify-between px-4 py-3 bg-slate-900 border-b border-slate-700/50 font-medium">
      <div className="flex gap-4">
        <Link to={getHomePath()} className="text-slate-300 hover:text-white transition-colors">Home</Link>
        {isOwner && <Link to="/dashboard" className="text-slate-300 hover:text-white transition-colors">Dashboard</Link>}
        {isOwner && isFeatureEnabled('requestWorkshop') && <Link to="/book-workshop" className="text-slate-300 hover:text-white transition-colors">Request Workshop</Link>}
        {isOwner && isFeatureEnabled('requestQuotation') && <Link to="/quotation/request" className="text-slate-300 hover:text-white transition-colors">Request Quotation</Link>}
        {isOwner && isFeatureEnabled('requestLoader') && <Link to="/request-loader" className="text-slate-300 hover:text-white transition-colors">Request Loader</Link>}
        {isOwner && isFeatureEnabled('carReception') && <Link to="/owner/checklists" className="text-slate-300 hover:text-white transition-colors">My Checklists</Link>}
        {user?.role === 'spare parts supplier' && <Link to="/supplier" className="text-slate-300 hover:text-white transition-colors">Supplier</Link>}
        {user?.role === 'spare parts supplier' && <Link to="/supplier/accepted" className="text-slate-300 hover:text-white transition-colors">Accepted Quotes</Link>}
        {user?.role === 'insurance company' && <Link to="/insurance" className="text-slate-300 hover:text-white transition-colors">Insurance</Link>}
        {user?.role === 'vehicle loader' && <Link to="/loader" className="text-slate-300 hover:text-white transition-colors">Loader</Link>}
        {user?.role === 'admin' && <Link to="/admin/dashboard" className="text-slate-300 hover:text-white transition-colors">Admin Dashboard</Link>}
        {user?.role === 'admin' && <Link to="/admin/features" className="text-slate-300 hover:text-white transition-colors">Features</Link>}
      </div>
      {token && (
        <button onClick={() => { logout(); window.location.href = '/login'; }} className="text-red-400 hover:text-red-300 transition-colors">
          Logout
        </button>
      )}
    </nav>
  );
};

// Inner app — needs to be inside Router to use hooks
const AppInner = () => {
  const { user, token, logout } = useAuth();
  const isStandalone = useIsStandalone();

  const getHomePath = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'car owner': return '/dashboard';
      case 'car workshop': return '/workshop';
      case 'spare parts supplier': return '/supplier';
      case 'insurance company': return '/insurance';
      case 'vehicle loader': return '/loader';
      case 'admin': return '/admin/dashboard';
      default: return '/';
    }
  };

  return (
    <div className="min-h-0 bg-slate-950 text-gray-100">
      {/* Meta Pixel: fire PageView on every route change (SPA) */}
      <MetaPixelTracker />
      {/* Global nav — hidden on pages that have their own (Home, Login, Signup, etc.) */}
      {!isStandalone && (
        <GlobalNav user={user} token={token} logout={logout} getHomePath={getHomePath} />
      )}

            <Routes>
              {/* --- DEFAULT ROUTE --- */}
              <Route path="/" element={<Home />} />

              {/* --- PUBLIC --- */}
              <Route path="/signup" element={<Signup />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/activate/:token" element={<ActivateAccount />} />
              <Route path="/workshops/:id" element={<WorkshopProfile />} />
              <Route path="/workshop/:id" element={<PublicWorkshopProfile />} />
              <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/payment-error" element={<PaymentErrorPage />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/mission" element={<Mission />} />
              <Route path="/about" element={<About />} />
              <Route path="/conditions" element={<Conditions />} />
              <Route path="/confidentialite" element={<Confidentialite />} />
              {/* Google Play requires a data-deletion URL reachable WITHOUT
                  signing in — a reviewer has no account. Public route. */}
              <Route path="/suppression-compte" element={<SuppressionCompte />} />
              <Route path="/services" element={<ServicesSection />} />

              {/* --- ADMIN --- */}
              <Route element={<ProtectedRoute roles={['admin']}><AdminLayout /></ProtectedRoute>}>
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/catalog" element={<CatalogValidation />} />
                <Route path="/admin/part-validations" element={<AdminPartValidations />} />
                <Route path="/admin/feature-flags" element={<AdminFeatureFlags />} />
                <Route path="/admin/features" element={<FeatureManagement />} />
                <Route path="/admin/user-validation" element={<UserValidation />} />
                <Route path="/admin/create-account" element={<CreateAccount />} />
                <Route path="/admin/telematics" element={<TelematicsDevices />} />
                <Route path="/admin/crashes" element={<AdminCrashes />} />
              </Route>

              {/* --- OWNER --- */}
              <Route element={<ProtectedRoute roles={['car owner']}><OwnerLayout /></ProtectedRoute>}>
                <Route path="/dashboard" element={<OwnerDashboard />} />
                <Route path="/profile" element={<Profile token={token} />} />

                <Route path="/dashboard/invoices" element={<FeatureGate feature="clientInvoices"><ClientSupplierInvoicesList /></FeatureGate>} />

                <Route path="/vehicles" element={<FeatureGate feature="myVehicles"><VehicleList token={token} /></FeatureGate>} />
                <Route path="/vehicle-page" element={<FeatureGate feature="myVehicles"><VehiclePage token={token} /></FeatureGate>} />
                <Route path="/vehicles/:id" element={<FeatureGate feature="myVehicles"><VehicleDetail /></FeatureGate>} />
                <Route path="/add-vehicle" element={<FeatureGate feature="myVehicles"><AddVehicle token={token} /></FeatureGate>} />

                <Route path="/book-workshop" element={<FeatureGate feature="requestWorkshop"><RequestWorkshop /></FeatureGate>} />
                <Route path="/quotation/request" element={<FeatureGate feature="requestQuotation"><RequestQuotation token={token} /></FeatureGate>} />
                <Route path="/owner/supplier-quotations" element={<FeatureGate feature="supplierQuotations"><OwnerSupplierQuotationsPage /></FeatureGate>} />
                <Route path="/request-loader" element={<FeatureGate feature="requestLoader"><RequestLoader token={token} /></FeatureGate>} />

                <Route path="/vehicle/:vehicleId/history" element={<FeatureGate feature="myVehicles"><MaintenanceHistoryWrapper /></FeatureGate>} />
                <Route path="/ai-assistant" element={<FeatureGate feature="aiAssistant"><WithVehicleProvider><OemAssistant token={token} /></WithVehicleProvider></FeatureGate>} />

                <Route path="/owner/checklists" element={<FeatureGate feature="carReception"><OwnerChecklistViewer /></FeatureGate>} />
                <Route path="/owner/reception-history" element={<FeatureGate feature="carReception"><ReceptionHistory /></FeatureGate>} />

                <Route path="/owner/quotations" element={<FeatureGate feature="myQuotations"><OwnerQuotationsPage /></FeatureGate>} />
                <Route path="/owner/invoices" element={<FeatureGate feature="myInvoices"><OwnerInvoicesPage /></FeatureGate>} />
                <Route path="/owner/invoices/:invoiceId" element={<FeatureGate feature="myInvoices"><SupplierInvoiceView /></FeatureGate>} />
                <Route path="/owner/view-maintenance/:id" element={<ViewMaintenance />} />
                <Route path="/owner/invoice/from-quotation/:id" element={<FeatureGate feature="myInvoices"><OwnerInvoiceFromQuotationPage /></FeatureGate>} />
                <Route path="/owner/invoice/:id" element={<FeatureGate feature="myInvoices"><OwnerInvoiceByIdPage /></FeatureGate>} />
              </Route>

              {/* --- WORKSHOP --- */}
              <Route element={<ProtectedRoute roles={['car workshop']}><WorkshopLayout /></ProtectedRoute>}>
                <Route path="/workshop" element={<WorkshopDashboard />} />
                <Route path="/workshop/walk-in" element={<WalkInRegistration />} />
                <Route path="/workshop/log-maintenance" element={<WithVehicleProvider><WorkshopLogMaintenance /></WithVehicleProvider>} />
                <Route path="/workshop/ledger" element={<WithVehicleProvider><CashbookLedger /></WithVehicleProvider>} />
                <Route path="/workshop/finance" element={<CashbookForm />} />
                <Route path="/workshop/categories" element={<CategoryManager />} />
                <Route path="/workshop/checklist" element={<VehicleReceptionForm />} />
                <Route path="/workshop/checklist/view" element={<ChecklistViewer />} />
                <Route path="/workshop/reception-checklist" element={<ReceptionChecklist />} />
                <Route path="/workshop/quotations" element={<WorkshopQuotationsPage token={token} />} />
                <Route path="/workshop/requests" element={<ViewRequests />} />
                <Route path="/workshop/catalog" element={<WorkshopCatalog />} />
                <Route path="/workshop/invoices" element={<WorkshopInvoicesPage token={token} />} />
                <Route path="/workshop/receipts" element={<PaymentReceiptsPage token={token} />} />
                <Route path="/workshop/delivery-notes" element={<DeliveryNotesPage token={token} />} />
                <Route path="/workshop-ratings" element={<WorkshopRatings />} />
                <Route path="/view-maintenance/:id" element={<ViewMaintenance />} />
                <Route path="/workshop/purchase-orders" element={<WorkshopPurchaseOrdersPage />} />
                {/* Parts sourcing: the workshop raises an RFQ to suppliers and
                    compares their replies. The backend has supported a
                    workshop-raised RFQ all along (requestType 'workshop');
                    these are the two missing doors. Both pages are shared with
                    the owner space and switch on role. */}
                <Route path="/workshop/parts-request" element={<RequestQuotation token={token} />} />
                <Route path="/workshop/supplier-quotations" element={<OwnerSupplierQuotationsPage />} />
                <Route path="/workshop/history/:vehicleId" element={<WorkshopVehicleHistory />} />
                <Route path="/workshop/profile" element={<WorkshopPrivateProfile />} />
                <Route path="/profile" element={<Profile token={token} />} />
              </Route>

              {/* --- SUPPLIER --- */}
              <Route element={<ProtectedRoute roles={['spare parts supplier']}><SupplierLayout /></ProtectedRoute>}>
                <Route path="/supplier" element={<SupplierDashboard token={token} />} />
                <Route path="/supplier/accepted" element={<AcceptedResponsesViewer token={token} />} />
                {/* Where a supplier answers a buyer counter-offer. The page
                    existed but was never routed, so negotiations arrived
                    with no way to reply. */}
                <Route path="/supplier/quotes" element={<SupplierMyQuotesPage token={token} />} />
                <Route path="/respond-quotation/:id" element={<RespondQuotation token={token} />} />
                <Route path="/supplier/purchase-orders" element={<SupplierPurchaseOrdersPage />} />
                <Route path="/supplier/receipts" element={<PaymentReceiptsPage token={token} />} />
                <Route path="/supplier/invoices" element={<SupplierInvoicesList />} />
                <Route path="/supplier/invoices/:invoiceId" element={<SupplierInvoiceView />} />
                <Route path="/supplier/profile" element={<Profile token={token} />} />
              </Route>

              {/* --- INSURANCE --- */}
              <Route path="/insurance" element={<ProtectedRoute roles={['insurance company']}><InsuranceDashboard /></ProtectedRoute>} />

              {/* --- LOADER --- */}
              <Route path="/loader" element={<ProtectedRoute roles={['vehicle loader']}><LoaderDashboard /></ProtectedRoute>} />

              {/* --- 404 --- */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <PermissionsProvider>
      <Router>
        <ScrollToTop />
        <AppInner />
      </Router>
    </PermissionsProvider>
    <ReactQueryDevtools initialIsOpen={false} />
  </QueryClientProvider>
);

export default App;
